import { useCallback } from 'react'

import { StartChargingState, StartChargingStep } from './types'
import { Action } from './reducer'
import { ChargingPaymentMethod } from '@/types/Payment'

interface UseStartChargingActionsProps {
  dispatch: React.Dispatch<Action>
  state: StartChargingState
  hasPendingBalance: boolean
  hasIdleRate: boolean
  initialStep: StartChargingStep
  onConfirm: (state: StartChargingState) => Promise<void>
  onCancel: () => void
}

export const useStartChargingActions = ({
  dispatch,
  state,
  hasPendingBalance,
  hasIdleRate,
  initialStep,
  onConfirm,
  onCancel,
}: UseStartChargingActionsProps) => {
  const setAmount = useCallback(
    (amount: number) => {
      dispatch({ type: 'SET_AMOUNT', payload: amount })
    },
    [dispatch]
  )

  const setPaymentMethod = useCallback(
    (method: ChargingPaymentMethod | null, cardId?: number) => {
      dispatch({ type: 'SET_PAYMENT_METHOD', payload: { method, cardId } })
      const nextStep = hasIdleRate ? StartChargingStep.IDLE_RATE_CONFIRMATION : StartChargingStep.CONFIRMATION
      dispatch({ type: 'GO_TO_STEP', payload: nextStep })
    },
    [dispatch, hasIdleRate]
  )

  const setUseWalletBalance = useCallback(
    (useWalletBalance: boolean) => {
      dispatch({ type: 'SET_USE_WALLET_BALANCE', payload: useWalletBalance })
    },
    [dispatch]
  )

  const setProcessing = useCallback(
    (isProcessing: boolean) => {
      dispatch({ type: 'SET_PROCESSING', payload: isProcessing })
    },
    [dispatch]
  )

  const goToStep = useCallback(
    (step: StartChargingStep) => {
      dispatch({ type: 'GO_TO_STEP', payload: step })
    },
    [dispatch]
  )

  const goBack = useCallback(() => {
    dispatch({ type: 'GO_BACK', payload: { hasPendingBalance } })
  }, [dispatch, hasPendingBalance])

  const goForward = useCallback(() => {
    dispatch({ type: 'GO_FORWARD' })
  }, [dispatch])

  const resetFlow = useCallback(() => {
    dispatch({ type: 'RESET', payload: { initialStep } })
  }, [dispatch, initialStep])

  const cancel = useCallback(() => {
    resetFlow()
    onCancel()
  }, [resetFlow, onCancel])

  const confirmPayment = useCallback(async () => {
    setProcessing(true)

    try {
      await onConfirm(state)
    } catch {
      setProcessing(false)
    }
  }, [state, onConfirm, setProcessing])

  return {
    setAmount,
    setPaymentMethod,
    setProcessing,
    goToStep,
    goBack,
    goForward,
    resetFlow,
    cancel,
    confirmPayment,
    setUseWalletBalance,
  }
}
